import { VideoCameraAddOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Popover, Row, Form, Tooltip } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { triggerJitsiEmail } from '../../../store/actions/utilAction';
import { ApplicationDetailModel } from '../../../store/models/applicationDetailModel';
import { base64UrlEncode, centeredNotification, getUniqueMeetId } from '../../../utils/utility';
import { GUTTOR_SPACE_SMALL } from '../../constants/commonConstants';

const JitsiDialer = (): ReactElement => {
  const configuredPayers: any[] = useSelector((state: RootState): any => state?.payerList?.allPayers || []);
  const [openMeetQuestions, setOpenMeetQuestions] = useState(false);
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const appDetail = useSelector((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const [selectedPayerDetail, setSelectedPayerDetail] = useState(null as any);
  const [isLoading, setIsLoading] = useState(false);
  const [listOfEnabledPayers, setListOfEnabledPayers] = useState([] as any);

  const onSelectionHandler = (element: any): any => {
    setSelectedPayerDetail(element);
    setOpenMeetQuestions(true);
  };

  const formOnSubmit = (): any => {
    form.validateFields().then((): any => {
      const payload = {
        meetingLink: getUniqueMeetId(appDetail.getProviderId(), appDetail.getUserId(), selectedPayerDetail.uniqueIdentifier),
        reason: form.getFieldValue('reason'),
        userEmail: appDetail.getUserEmail(),
        payerId: selectedPayerDetail.id,
        productCode: selectedPayerDetail.productCode,
      };
      setIsLoading(true);
      dispatch(triggerJitsiEmail(payload, onSuccess, onError));
    });
  };

  const onSuccess = (payload: any = {}): any => {
    setIsLoading(false);
    const encodedParams = base64UrlEncode(JSON.stringify({
      ...payload,
      userName: appDetail.getUserName(),
    }));

    window.open(`/jitsimeet/${encodedParams}`, '_blank');
    onModalClose();
  };

  const onError = (errorMsg: string): any => {
    setIsLoading(false);
    centeredNotification(errorMsg);
  };

  const onModalClose = (): any => {
    setOpenMeetQuestions(false);
    form.resetFields();
  };

  const modalContent = (
    <Form
      form={form}
      layout="vertical"
    >
      <Form.Item name="reason" label="Why do you want to connect to payer ?" rules={[{ required: true, message: 'Reason is required' }]}>
        <TextArea rows={4} data-testid="reason-area"/>
      </Form.Item>
      <Row gutter={GUTTOR_SPACE_SMALL} className="text-right">
        <Col md={24} className="text-right">
          <Button className="mr-10" onClick={onModalClose}>Cancel</Button>
          <Button type="primary" data-testid="connect" onClick={formOnSubmit} loading={isLoading}>Connect</Button>
        </Col>
      </Row>
    </Form>
  );

  useEffect((): any => {
    setListOfEnabledPayers(configuredPayers.filter(({isVideoCallEnabled}: any): boolean => isVideoCallEnabled));
  }, [configuredPayers]);

  const content = (): any => {
    return (
      <React.Fragment>
        <Row gutter={GUTTOR_SPACE_SMALL}>
          {listOfEnabledPayers.length > 0 ? listOfEnabledPayers.map((payer: any): any => (
            <Col key={payer.uniqueIdentifier}>
              <Tooltip title={payer.name === payer.shortName ? null : payer.name} placement="top">
                <Button
                  data-testid={`payer-${payer.shortName}`}
                  type="default"
                  onClick={(): any => onSelectionHandler(payer)}
                >
                  {payer.shortName || payer.name}
                </Button>
              </Tooltip>
            </Col>
          )) :
            <Col>No configured payers for call.</Col>
          }
        </Row>
      </React.Fragment>
    );
  };

  return (
    <div className="jitsi-dialer">
      <Tooltip title="Connect with payer" placement="topRight">
        <Popover trigger="click"
          content={content()}
          placement="topRight"
          title={listOfEnabledPayers.length ? 'Setup a call with' : null}>
          <Button data-testid="jitsi-call" type="primary" shape="circle" icon={<VideoCameraAddOutlined />} size="large" />
        </Popover>
      </Tooltip>
      <Modal
        visible={openMeetQuestions}
        footer={null}
        title="Reason for the connect"
        onCancel={onModalClose}
      >
        {modalContent}
      </Modal>
    </div>
  );
};
export default JitsiDialer;
